import "../../../assets/css/tabs/documentCompliance.css"

import clientIcon from "../../../assets/images/inventory.svg"
import infoIcon from "../../../assets/images/info.svg"

import React, { useEffect, useRef, useState } from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import DocumentInformationModal from "./documentInformationModal";
import { legalDocumentIDs, extraInfoIcon } from "./utils";
import { useDocumentComplianceData, useDocumentComplianceMutations } from "../../suppliers/suppliersProfile/hooks";

export default function DocumentComplianceTable(props) {
	// VARIABLES RECIEVED FROM PROPS
	const { supplier, serverDate, userLogged } = props

	// VARIABLE TO CONTROL THE SELECTED YEAR TO SHOW
	const [selectedYear, setSelectedYear] = useState(null)

	// VARIABLE TO CONTROL THE SELECTED CLIENT TO SHOW
	const [selectedClientID, setSelectedClientID] = useState(null)

	// VARIABLE TO CONTROL THE DOCUMENT INFORMATION MODAL
	const [showDocumentModal, setShowDocumentModal] = useState(null)
	const [modalData, setModalData] = useState(null)

	// REFERENCE TO KNOW WICH SQUARE IS SELECTED
	const docSquareRef = useRef(null)

	// REACT QUERY DATA AND MUTATIONS
	const { complianceData } = useDocumentComplianceData({ supplierId: supplier?._id, userLogged })
	const { handleUploadDocument, handleDeleteDocument, handleUpdateProperty, handleCreateOnce, handleRequestReview } =
		useDocumentComplianceMutations({ supplierId: supplier?._id })

	// DERIVED VARIABLES FROM COMPLIANCE DATA
	const yearsToShow = complianceData?.yearsToShow ?? []
	const monthlyCompliancePercent = complianceData?.monthlyCompliancePercent ?? []
	const documentReference = complianceData?.documentReference?.legalDocumentsToShow ?? []
	const documentComplianceList = complianceData?.documentComplianceList?.legalSupplierDocuments ?? []
	const futureDocuments = complianceData?.futureDocuments?.legalFutureDocuments ?? []
	const clientDocumentReference = complianceData?.documentReference?.clientDocumentsToShow ?? []
	const clientDocumentComplianceList = complianceData?.documentComplianceList?.clients ?? []
	const clientFutureDocuments = complianceData?.futureDocuments?.clientFutureDocuments ?? []

	useEffect(() => {
		document.body.addEventListener("click", unselectDocSquare)

		return function cleanUp() {
			document.body.removeEventListener("click", unselectDocSquare)
		}
	}, [])

	useEffect(() => {
		if (!complianceData) return
		const years = complianceData.yearsToShow ?? []
		const entities = complianceData.documentReference?.clientDocumentsToShow ?? []

		if (years.length > 0 && !years.includes(selectedYear)) {
			setSelectedYear(years[0])
		}
		if (entities.length > 0 && !entities.some(e => e.entityID?.toString() === selectedClientID?.toString())) {
			setSelectedClientID(entities[0].entityID)
		}
	}, [complianceData])

	// FUNCTION TO UNSELECT THE SQUARE DOC
	const unselectDocSquare = (e) => {
		const elements = Array.from(document.body.getElementsByClassName("squareContainer"))
		elements.forEach(element => element.classList.remove("active"))

		if (docSquareRef.current && docSquareRef.current.contains(e.target)) {
			docSquareRef.current.parentElement.classList.toggle("active")
		}
	}

	//FUNCTION TO OPEN THE MODAL FOR THE SQUARE DOC SELECTED
	const handleOpenDocumentModal = (target, documentReference, documentData, newOnceDocument, clientDocument) => {
		docSquareRef.current = target
		setModalData({
			documentReference: documentReference,
			documentData: documentData,
			newOnceDocument: newOnceDocument,
			clientDocument: clientDocument,
		})
		setShowDocumentModal(true)
	}

	// FUNCTION TO GET THE DOC SQUARE JSX
	const handleGetDocumentSquare = (documentReference, monthRecived, clientDocument) => {
		let documentFound, futureDocument

		if (!clientDocument) {
			// VERIFY EXISTING DOCUMENT
			documentFound = documentComplianceList.filter(document =>
				document.yearCreatedAt === selectedYear).find(document =>
					documentReference._id.toString() === document.documentID.toString() && monthRecived === document.monthCreatedAt)

			// VERIFY FUTURE DOCUMENT
			futureDocument = futureDocuments.filter(document =>
				document.yearCreatedAt === selectedYear).find(document =>
					documentReference._id.toString() === document.documentID.toString() && monthRecived === document.monthCreatedAt)
		} else {
			// VERIFY EXISTING CLIENT DOCUMENT
			documentFound = clientDocumentComplianceList.find(client =>
				client.clientID?.toString() === selectedClientID?.toString())?.clientSupplierDocuments?.filter(document =>
					document.yearCreatedAt === selectedYear).find(document =>
						documentReference._id?.toString() === document.documentID?.toString() && monthRecived === document.monthCreatedAt)


				// VERIFY FUTURE CLIENT DOCUMENT
				futureDocument = clientFutureDocuments.find(client =>
					client.entityID?.toString() === selectedClientID?.toString())?.futureDocuments?.filter(document =>
					document.yearCreatedAt === selectedYear).find(document =>
						documentReference._id.toString() === document.documentID.toString() && monthRecived === document.monthCreatedAt)
		}

		const docIsRepse = documentReference._id.toString() === legalDocumentIDs.repseFisica || documentReference._id.toString() === legalDocumentIDs.repseMoral
		const docIsActaOriginal = documentReference._id.toString() === legalDocumentIDs.actaConstitutivaOriginalMoral
		const docIsIDRepresentanteLegal = documentReference._id.toString() === legalDocumentIDs.idRepresentanteLegalMoral

		const docDueDate = docIsRepse ? "36 Months" :
			docIsIDRepresentanteLegal ? "12 Months" : "Once"

		// TEMPORAL OBJECT TO CHANGE A "ONCE" DOCUMENT
		const temporalDocument = {
			documentID: documentReference._id,
			documentName: documentReference.documentName,
			document: null,
			uploadedAt: null,
			comment: null,
			compliant: false,
			yearCreatedAt: serverDate.year,
			monthCreatedAt: serverDate.month,
			dayCreatedAt: serverDate.day,
			validated: false,
			dueDate: docDueDate,
			noActions: false,
		}

		const reviewRequestIcon = documentFound?.reviewRequest ? "reviewRequestIcon" : ""

		if (documentFound) {
			return (
				<OverlayTrigger
					overlay={
						<Tooltip id={`tooltipDocumentName`} style={{ position: "fixed" }}>
							{documentFound.compliant ? "Compliant" :
								documentFound.reviewRequest ? "Review requested" :
								documentFound.document ? "Pending Review" : "Non-Compliant"
							}
							<br />
							Last Uploaded: {documentFound.uploadedAt ? documentFound.uploadedAt : "N/A"}
						</Tooltip>
					}>
					<div className={`documentSquare ${documentFound.documentStatus} ${reviewRequestIcon}`} onClick={(e) => handleOpenDocumentModal(e.target, documentReference, documentFound, false, clientDocument)} />
				</OverlayTrigger>
			)
		} else if (futureDocument) {
			return (
				<OverlayTrigger
					overlay={
						<Tooltip id={`tooltipDocumentName`} style={{ position: "fixed" }}>
							Future Pending Document
						</Tooltip>
					}>
					<div className={`documentSquare ${futureDocument.documentStatus}`} />
				</OverlayTrigger>
			)
		} else if (monthRecived === serverDate.month && (documentReference.periodicity === 0 || docIsRepse || docIsIDRepresentanteLegal) && !docIsActaOriginal) {
			// } else if (monthRecived === serverDate.month && (documentReference.periodicity === 0 || docIsRepse) && !docIsActaOriginal) {
			return (
				<OverlayTrigger
					overlay={
						<Tooltip id={`tooltipDocumentName`} style={{ position: "fixed" }}>
							You can update this document if necessary
						</Tooltip>
					}>
					<div className={`documentSquare noDocumentNeeded`} onClick={(e) => handleOpenDocumentModal(e.target, documentReference, temporalDocument, true, clientDocument)} />
				</OverlayTrigger>
			)
		} else {
			return (
				<OverlayTrigger
					overlay={
						<Tooltip id={`tooltipDocumentName`} style={{ position: "fixed" }}>
							No actions Needed
						</Tooltip>
					}>
					<div className={`documentSquare noDocumentNeeded`} />
				</OverlayTrigger>
			)
		}
	}

	// FUNCTION TO SAVE THE DOCUMENT INFORMATION
	const changeDocumentInformation = async (docUniqueId, property, newValue, clientDocument) => {
		try {
			if (property === "document") {
				if (!newValue) {
					await handleDeleteDocument({ docId: docUniqueId })
				} else {
					await handleUploadDocument({
						docId: docUniqueId,
						file: newValue,
						rfc: supplier.informationCompliance.generalInformation.rfc,
						companyName: supplier.informationCompliance.generalInformation.companyName,
					})
				}
			} else {
				// dataID y rol viajan para que el backend pueda crear la alerta de
				// comentario nuevo a nombre de quien lo escribio.
				await handleUpdateProperty({
					docId: docUniqueId,
					property,
					value: newValue,
					dataID: userLogged?.dataID,
					rol: userLogged?.rol,
				})
			}

			if (property !== "comment" && (newValue || property !== "document")) {
				setShowDocumentModal(false)
			}

			return true
		} catch (err) {
			// Error handled by mutation hook
			return false
		}
	}

	// FUNCTION TO SAVE THE NEW ONCE DOCUMENT AND CHANGE THE PAST ONE TO THE HISTORIC
	const saveNewOnceDocument = async (newDocument, documentFile, clientDocument) => {
		try {
			await handleCreateOnce({
				supplierID: supplier._id,
				file: documentFile,
				sourceDocumentID: newDocument.documentID,
				entityID: clientDocument ? selectedClientID : null,
				rfc: supplier.informationCompliance.generalInformation.rfc,
				companyName: supplier.informationCompliance.generalInformation.companyName,
			})
			setShowDocumentModal(false)
		} catch (err) {
			// Error handled by mutation hook
		}
	}

	// CALLBACK FOR REQUEST REVIEW FROM MODAL
	const handleOnRequestReview = async (docId, username, clientDocument) => {
		await handleRequestReview({ docId, username, clientDocument })
	}

	// HELPER: returns true if the entity has at least one supplierCompliancy doc with no uploaded file
	const entityHasMissingRequiredDocs = (entityID) => {
		const entityRef = clientDocumentReference.find(e => e.entityID?.toString() === entityID?.toString())
		if (!entityRef) return false
		const requiredDocs = entityRef.documentsToShow.filter(d => d.supplierCompliancy)
		if (!requiredDocs.length) return false
		const entityCompliance = clientDocumentComplianceList.find(c => c.clientID?.toString() === entityID?.toString())
		const uploadedDocIDs = new Set(
			(entityCompliance?.clientSupplierDocuments ?? [])
				.filter(d => d.document)
				.map(d => d.documentID?.toString())
		)
		return requiredDocs.some(d => !uploadedDocIDs.has(d._id?.toString()))
	}

	return (
		<section className="documentCompliance">
			<div className="tableHeader">
				<h5 className="title">Documents</h5>
				<div className="labels">
					<span className="withDot green">Compliant</span>
					<span className="withDot white">No actions needed</span>
					<span className="withDot red">Non-compliant</span>
					<span className="withDot grey">Pending month</span>
					<span className="withDot yellow">Pending review</span>
					<span className="withDot darkYellow">Comment</span>
				</div>

				<Dropdown className="yearButton">
					<Dropdown.Toggle>
						{selectedYear}
					</Dropdown.Toggle>

					<Dropdown.Menu>
						{yearsToShow.map((year, index) => (
							<Dropdown.Item key={index} onClick={(e) => setSelectedYear(+e.target.innerText)}>{year}</Dropdown.Item>
						))}
					</Dropdown.Menu>
				</Dropdown>
			</div>

			<div className="tableInformation">
				<div className="tableLeftSide">
					<div className="tableTitle">
						<span className="documentNumber"></span>
						<span className="documentName">Name</span>
					</div>
					{documentReference.map((reference, index) => (
						<div key={index} className="tableRow">
							<span className="documentNumber">{index + 1 < 10 ? `0${index + 1}` : index + 1}</span>
							<OverlayTrigger
								key={index}
								overlay={
									<Tooltip id={`tooltipDocumentName`} style={{ position: "fixed" }}>
										{reference.documentName}
									</Tooltip>
								}>
								<span className="documentName">{reference.documentName}</span>

							</OverlayTrigger>
							{extraInfoIcon.includes(reference._id) && 
								<OverlayTrigger
									overlay={
										<Tooltip id={`tooltipExtraIcon`} style={{ position: "fixed" }}>
											<span className="documentExtraInfo">Documents for suppliers with active customers within Cattenna</span>
										</Tooltip>
									}>
										<img className="extraInfoIcon" src={infoIcon} alt="InfoIcon" />
								</OverlayTrigger>
							}
						</div>
					))}

					<div className="tableFooter">
						<span className="footerTitle">Monthly percent compliancy</span>
					</div>
				</div>

				<div className="tableRightSide">
					<div className="tableTitle">
						{monthlyCompliancePercent.find(yearPercent => yearPercent?.year === selectedYear)
							?.months.map((month, index) => (
								<span key={index} className={`documentMonth ${selectedYear === serverDate?.year && month.month === serverDate.month ? "current" : ""}`}>
									{month.shortLabel}
								</span>
							))}
					</div>

					{documentReference.map((reference, referenceIndex) => (
						<div key={referenceIndex} className="tableRow">
							{monthlyCompliancePercent.find(yearPercent => yearPercent?.year === selectedYear)
								?.months.map((month, monthIndex) => (
									<div key={referenceIndex + monthIndex} className={`squareContainer ${selectedYear === serverDate?.year && month.month === serverDate.month ? "current" : ""}`}>
										{handleGetDocumentSquare(reference, month.month, false)}
									</div>
								))}
						</div>
					))}

					<div className="tableFooter">
						{monthlyCompliancePercent.find(yearPercent => yearPercent?.year === selectedYear)
							?.months.map((month, index) => (
								<span key={index} className={`monthPercent ${selectedYear === serverDate?.year && month.month === serverDate.month ? "current" : ""}`}>
									{month.percent.toFixed(2)}%
								</span>
							))}
					</div>
				</div>
			</div>

			{clientDocumentReference.length > 0 &&
				<React.Fragment>
					<div className="tableHeader">
						<h5 className="title">Documents by client</h5>
						<React.Fragment>
							<img className="clientFilterIcon" src={clientIcon} alt="Prodensa Supplier" />
							<Dropdown className="clientSelector">
								<Dropdown.Toggle>
								<span className="clientSelectorName">
									{clientDocumentReference.find(client =>
										client.entityID?.toString() === selectedClientID?.toString())?.entityName}
								</span>
								{entityHasMissingRequiredDocs(selectedClientID) && <span className="clientMissingIcon" />}
							</Dropdown.Toggle>

							<Dropdown.Menu>
								{clientDocumentReference.map((entity, index) => (
									<Dropdown.Item key={index} onClick={() => setSelectedClientID(entity.entityID)}>
										<span className="clientSelectorItemName">{entity.entityName}</span>
										{entityHasMissingRequiredDocs(entity.entityID) && <span className="clientMissingDot" />}
									</Dropdown.Item>
									))}
								</Dropdown.Menu>
							</Dropdown>
						</React.Fragment>
					</div>

					<div className="tableInformation">
						<div className="tableLeftSide">
							<div className="tableTitle">
								<span className="documentNumber"></span>
								<span className="documentName">Name</span>
							</div>
							{clientDocumentReference.find(client => client.entityID?.toString() === selectedClientID?.toString())?.documentsToShow?.map((reference, index) => (
								<div key={index} className="tableRow">
									<span className="documentNumber">{index + 1 < 10 ? `0${index + 1}` : index + 1}</span>
									<OverlayTrigger
										key={index}
										overlay={
											<Tooltip id={`tooltipDocumentName`} style={{ position: "fixed" }}>
												{reference.documentName}
											</Tooltip>
										}>
										<span className="documentName">{reference.documentName}</span>
									</OverlayTrigger>
								</div>
							))}

							<div className="tableFooter">
								<span className="footerTitle">Monthly percent compliancy</span>
							</div>
						</div>

						<div className="tableRightSide">
							<div className="tableTitle">
								{monthlyCompliancePercent.find(yearPercent => yearPercent?.year === selectedYear)
									?.months.map((month, index) => (
										<span key={index} className={`documentMonth ${selectedYear === serverDate?.year && month.month === serverDate.month ? "current" : ""}`}>
											{month.shortLabel}
										</span>
									))}
							</div>

							{clientDocumentReference.find(client => client.entityID?.toString() === selectedClientID?.toString())?.documentsToShow?.map((reference, referenceIndex) => (
								<div key={referenceIndex} className="tableRow">
									{monthlyCompliancePercent.find(yearPercent => yearPercent?.year === selectedYear)
										?.months.map((month, monthIndex) => (
											<div key={referenceIndex + monthIndex} className={`squareContainer ${selectedYear === serverDate?.year && month.month === serverDate.month ? "current" : ""}`}>
												{handleGetDocumentSquare(reference, month.month, true)}
											</div>
										))}
								</div>
							))}

							<div className="tableFooter">
								{monthlyCompliancePercent.find(yearPercent => yearPercent?.year === selectedYear)
						?.clients?.find(client => client.entityID?.toString() === selectedClientID?.toString())?.months?.map((month, index) => (
										<span key={index} className={`monthPercent ${selectedYear === serverDate?.year && month.month === serverDate.month ? "current" : ""}`}>
											{month.percent.toFixed(2)}%
										</span>
									))}
							</div>
						</div>
					</div>
				</React.Fragment>}

			{modalData && <DocumentInformationModal
				show={showDocumentModal}
				onHide={() => setShowDocumentModal(false)}
				documentData={modalData}
				changeDocumentInformation={changeDocumentInformation}
				supplier={supplier}
				supplierRfc={supplier.informationCompliance.generalInformation.rfc}
				saveNewOnceDocument={saveNewOnceDocument}
				darkLightMode={props.darkLightMode}
				docsRefs={documentReference}
				legalDocs={complianceData?.documentComplianceList?.legalSupplierDocuments ?? []}
				onRequestReview={handleOnRequestReview}
			/>}
		</section >
	)
}
